import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sp] = useSearchParams();
  const reason = sp.get("reason");

  const submit = async (e) => {
    e.preventDefault();
    // Read directly from the DOM so browser autofill can never desync from React state
    const emailVal = (e.target.elements.email?.value ?? email).trim();
    const pwVal = (e.target.elements.password?.value ?? password).trim();
    setError(""); setLoading(true);
    try {
      await login(emailVal, pwVal);
      navigate("/");
    } catch (err) {
      // Generische, lokalisierte Meldung: Auth-Fehler dürfen nicht verraten, welcher Teil falsch war.
      const st = err?.response?.status;
      // 403 = fachliche Sperre (Konto/Mandant) — die Server-Meldung ist hier hilfreich und
      // verrät keine Anmeldeinformationen. 401/429 bleiben absichtlich generisch.
      const detail = err?.response?.data?.detail;
      setError(st === 429 ? t("errors.tooManyAttempts")
        : (st === 403 && typeof detail === "string" && detail) ? detail : t("login.failed"));
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden lg:flex w-1/2 bg-primary text-primary-foreground flex-col justify-between p-12">
        <div className="flex items-center gap-2.5">
          <div className="flex h-11 items-center justify-center rounded-md bg-white px-2"><img src="/logo.webp" alt="Gluty-Free Club" className="h-8 w-auto" /></div>
          <span className="font-heading text-lg font-bold">Club Operations</span>
        </div>
        <div>
          <h1 className="font-heading text-4xl font-semibold leading-tight">{t("login.claim")}</h1>
          <p className="mt-4 max-w-md text-primary-foreground/80 text-sm leading-relaxed">{t("login.intro")}</p>
        </div>
        <div className="text-xs text-primary-foreground/60">{t("login.traits")}</div>
      </div>

      <div className="flex w-full lg:w-1/2 items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="flex h-11 items-center justify-center rounded-md bg-white px-2"><img src="/logo.webp" alt="Gluty-Free Club" className="h-8 w-auto" /></div>
            <span className="font-heading text-lg font-bold">Club Operations</span>
          </div>
          <h2 className="font-heading text-2xl font-semibold">{t("login.signIn")}</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">{t("login.hint")}</p>
          {reason && (
            <p className="mb-4 rounded-md border border-chart-3/50 bg-chart-3/15 px-3 py-2 text-sm font-medium text-foreground"
              data-testid="login-session-notice">
              {reason === "logout" ? t("login.loggedOut")
                : reason === "idle" ? t("login.idleLogout") : t("login.sessionExpired")}
            </p>
          )}
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">{t("login.email")}</Label>
              <Input id="email" name="email" data-testid="login-email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} required autoComplete="username" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">{t("login.password")}</Label>
              <Input id="password" name="password" data-testid="login-password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" className="mt-1.5" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full">
              {loading ? t("login.signingIn") : t("login.signIn")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
